<template>
  <div class="row">
    <!-- START Page heading -->
    <div class="columns small-12">
      <h3>{{ $t("products") }}</h3>
      <div class="flex">
        <div class="w-full">
          <input
            v-model="search"
            class="form-control mb-3"
            :placeholder="$t('search')"
          />
        </div>
        <div class="whitespace-nowrap mt-2 ml-2">
          <p>
            <strong>{{ products.length }}</strong> {{ $t("results") }}
          </p>
        </div>
      </div>

      <b-table
        :items="products"
        responsive
        :fields="fields"
        :sort-by.sync="sortBy"
        striped
      >
        <template #cell(expectedDeliveryDate)="">
          <!--          {{ formatDate(data.item.expectedDeliveryDate) }}-->
        </template>
        <template #cell(functionControl)="data">
          <div v-if="data.item.functionControlDelivery">
            <button
              class="btn btn-link"
              @click="
                openFunctionControlDelivery({
                  productID: data.item.productID,
                  orderID: data.item.orderID,
                })
              "
            >
              <i class="fas fa-external-link-alt"></i>
            </button>
          </div>
        </template>

        <template #cell(status)="data">
          <div>
            <div>
              {{ data.item.status }}
            </div>
            <vue-step-progress-indicator
              v-if="!data.item.status.includes('Cancelled')"
              :steps="['1', '2', '3', '4', '5']"
              :colors="colorData"
              :show-label="false"
              :active-step="
                getStatusNumber(data.item.status, data.item.attachments)
              "
              :is-reactive="false"
            />
          </div>
        </template>

        <template #cell(l_IC_F)="data">
          <div v-if="data.item.l_IC_F">
            <span
              :class="{
                'text-danger': data.item.l_IC_F !== 'OK',
                'text-success': data.item.l_IC_F === 'OK',
              }"
            >
              {{ data.item.l_IC_F }}
            </span>
          </div>
        </template>

        <template #cell(r_IC_F)="data">
          <div v-if="data.item.r_IC_F">
            <span
              :class="{
                'text-danger': data.item.r_IC_F !== 'OK',
                'text-success': data.item.r_IC_F === 'OK',
              }"
            >
              {{ data.item.r_IC_F }}
            </span>
          </div>
        </template>

        <template #cell(orderItemID)="data">
          <button
            class="btn btn-primary"
            :orderid="data.item.orderID"
            :order-itemid="data.item.orderItemID"
            @close="show = false"
            @click="
              openMessageDialog(
                data.item.orderID,
                data.item.orderItemID,
                data.item.serialID,
                data.item.personDescription,
                data.item,
              )
            "
          >
            <i class="fas fa-info"></i>
          </button>
        </template>
      </b-table>
    </div>
    <b-modal
      id="function-control-delivery"
      ref="function-control-delivery"
      hide-footer
      :title="
        $t('functionControlDeliveryForOrder', {
          selectedOrderID: selectedOrderID,
        })
      "
      size="xl"
    >
      <function-control-delivery
        :orderid-prop="selectedOrderID"
        :productid-prop="selectedProductID"
        @submit="closeModal"
      />
    </b-modal>
    <!-- END Page heading -->
    <div v-if="products.length < 1" class="columns small-12">
      <p class="text-center">
        <strong>{{ $t("noProductsFound") }}</strong>
      </p>
    </div>
    <order-messages
      :orderid="selectedMessageOrderID"
      :order-itemid="selectedMessageOrderItemID"
      :show="show"
      :person="selectedMessagePerson"
      :selected-order-item="selectedOrderItem"
      :sn="selectedMessageSN"
      @close="show = false"
    />
  </div>
</template>

<script>
import {getOpenProducts} from "@/services/product";
import functionControlDelivery from "@/components/functioncontrol/FunctionControlDelivery.vue";
import OrderMessages from "@/components/order/OrderMessages.vue";
import VueStepProgressIndicator from "vue-step-progress-indicator";
import {colorData} from "../products/productStatusColor.js";
import {formatProductDates} from "@/functions/products/formatProductDates";
import {filterObjectsBySearchString} from "@/functions/filtering/searchFilterObjectArray";

export default {
  name: "AccountActiveProducts",
  components: {
    OrderMessages,
    functionControlDelivery,
    VueStepProgressIndicator,
  },
  data() {
    return {
      products: [],
      sortBy: "dateTimeCreated",
      show: false,
      selectedMessageOrderID: null,
      selectedMessageOrderItemID: null,
      selectedMessageSN: null,
      selectedMessagePerson: null,
      fields: [
        {
          key: "orderID",
          label: this.$t("order"),
          sortable: true,
        },
        {
          key: "dateTimeCreated",
          label: this.$t("date"),
          sortable: true,
        },
        {
          key: "personDescription",
          label: this.$t("person2"),
          sortable: true,
        },
        {
          key: "productNumber",
          label: "PN",
          sortable: true,
        },
        {
          key: "variantID",
          label: "LR",
          sortable: true,
        },
        {
          key: "itemDescription",
          label: this.$t("product"),
          sortable: true,
        },
        {
          key: "serialID",
          label: "SN",
          sortable: true,
        },
        {
          key: "status",
          label: this.$t("status"),
          sortable: true,
        },
        {
          key: "attachments",
          label: this.$t("files"),
          sortable: true,
        },
        {
          key: "dateTimeShipped",
          label: this.$t("estimatedShipping"),
          sortable: true,
        },
        {
          key: "l_IC_F",
          label: "L IC F",
          sortable: true,
        },
        {
          key: "r_IC_F",
          label: "R IC F",
          sortable: true,
        },
        {
          key: "reference",
          label: "Ref.",
          sortable: true,
        },
        {
          key: "functionControl",
          label: "FC",
          sortable: true,
        },
        {
          key: "orderItemID",
          label: this.$t("Info"),
        },
      ],
      search: "",
      selectedOrderID: null,
      selectedProductID: null,
      selectedOrderItem: null,
    };
  },
  computed: {
    colorData() {
      return colorData;
    },
  },
  watch: {
    search() {
      this.getProducts(this.search);
    },
  },
  async created() {
    this.$store.commit("setCustomerID", {
      customerID: document.getElementById("customerid").value,
    });
    this.getProducts();
  },
  methods: {
    getStatusNumber(status, filecount) {
      if (status === "Impression: Taken") {
        return 1;
      }

      if (status === "Impression: Scanned") {
        if (filecount > 0) {
          return 2;
        } else {
          return 1;
        }
      }

      if (status.includes("Scheduling")) {
        return 2;
      }

      if (status === "Impression: Received") {
        return 2;
      }

      if (status.includes("Repair")) {
        return 3;
      }

      if (status.includes("Model")) {
        return 3;
      }

      if (status.includes("Production")) {
        return 3;
      }

      if (status.includes("End control")) {
        return 3;
      }

      if (status.includes("Shipping")) {
        return 3;
      }

      if (status.includes("Function control")) {
        return 4;
      }

      if (status.includes("Delivered")) {
        return 5;
      }

      return 0;
    },
    openMessageDialog(orderID, orderItemID, sn, person, orderItem) {
      this.selectedMessageOrderID = orderID;
      this.selectedMessageOrderItemID = orderItemID;
      this.selectedMessageSN = sn;
      this.selectedMessagePerson = person;
      this.selectedOrderItem = orderItem;
      this.show = true;
    },
    closeModal() {
      this.$refs["function-control-delivery"].hide();
      this.getProducts();
    },
    openFunctionControlDelivery({orderID, productID}) {
      this.selectedOrderID = orderID;
      this.selectedProductID = productID;
      this.$refs["function-control-delivery"].show();
    },
    async getProducts(search) {
      const fetchedProducts = await getOpenProducts();

      const dateFormattedProducts = fetchedProducts.map((product) =>
        formatProductDates({product}),
      );

      this.products = filterObjectsBySearchString({
        searchString: search ?? "",
        objects: dateFormattedProducts,
      });
    },
  },
};
</script>

<style>
.progress__bubble {
  height: 15px !important;
  width: 15px !important;
  border: 1px solid white !important;
  border-radius: 0% !important;
}
</style>
